import Image from 'next/image';
import Link from 'next/link';
import React, { useContext } from 'react';
import LocalStationLogo from '@/components/global/LocalStationLogo';
import { PbsKidsProperty } from '@/types/pbskids-graph';
import { StationLogoTheme } from '@/types/stations';
import LanguageLocalizer from '@/utils/language-localization';
import { findThemeByContextName, ResolvedPageThemes } from '@/utils/theming-system';
import styles from './ProducerFooter.module.scss';
import ProducerSponsors from './ProducerSponsors';
import SharedFooterLinks from './SharedFooterLinksView';
import ThemedModule from '@/components/global/ThemedModule';

type FooterProps = {
  propertyData: null | PbsKidsProperty,
  resolvedPageThemes?: ResolvedPageThemes,
  footerSticker?: string,
};

function SeriesCopyRight({ copyRightNotice, legalNotice }: {copyRightNotice?: string|null, legalNotice?: string|null}) {
  return <div className={styles.seriesCopyRightWrapper}>
    {copyRightNotice &&
      <h2 className={styles.copyRightNotice}>{copyRightNotice}</h2>
    }

    {legalNotice &&
      <div className={styles.legalNotice}
        dangerouslySetInnerHTML={{
          __html: legalNotice,
        }}
      ></div>
    }
  </div>;
}

export default function ProducerFooter({ propertyData, resolvedPageThemes, footerSticker }: FooterProps) {
  const { __t } = new LanguageLocalizer( useContext, 'components/global/SiteFooter' );
  const footerTheme = findThemeByContextName(resolvedPageThemes?.themes || [], 'footer')?.theme || resolvedPageThemes?.primaryTheme;
  const stationLogoTheme = footerTheme?.stationLogoStyle as StationLogoTheme || StationLogoTheme.White;
  const sponsorLogoTheme = footerTheme?.sponsorLogoStyle;
  footerSticker = footerSticker || propertyData?.footerStickerImage?.[0]?.url || '';

  return <ThemedModule
    backgroundMode='pattern'
    defaultWrapperClass={false}
    theme={footerTheme}
    themeContextName='footer'
    wrapperClassName={styles.outerWrapper}
  >

    <footer className={styles.mainContainer}>
      { !!(propertyData?.producers?.length ||
        propertyData?.majorFunders?.length ||
        propertyData?.additionalFunders?.length) &&
        <ProducerSponsors
          propertyData={propertyData}
          sponsorLogoTheme={sponsorLogoTheme}
        />
      }

      { (propertyData?.productionCopyrightNotice || propertyData?.legalNotice) &&
        <SeriesCopyRight
          copyRightNotice={propertyData.productionCopyrightNotice}
          legalNotice={propertyData.legalNotice}
        />
      }

      <LocalStationLogo
        logoTheme={stationLogoTheme}
        prependElement={
          <p>{__t('from')}</p>
        }
        wrapperClasslist={styles.logoWrapper}
        innerClasslist={styles.stationLogo}
        sizes='300px'
      />

      <ul className={styles.siteNavLinks}>
        <li><Link href='/games'><span className='uppercase'>PBS Kids</span> Games</Link></li>
        <li><Link href='/videos'><span className='uppercase'>PBS Kids</span> Videos</Link></li>
      </ul>

      <SharedFooterLinks />

      { footerSticker &&
        <Image
          className={styles.footerStickerImage}
          src={footerSticker}
          alt=''
          loading='lazy'
          priority={false}
          width={240}
          height={240}
          sizes='(min-width: 1280px) 240px, 160px'
        />
      }

    </footer>
  </ThemedModule>;
}
